'use es6';

import { GROUP, CHOICE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DEFAULT, CHOICES, CHILDREN } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { pick } from 'underscore';
import { BASE_ATTRIBUTES, ADDITIONAL_ATTRIBUTES, TRANSLATABLE_FIELD_ATTRIBUTES, SPECIAL_DEFAULT_CHILD_PROPERTIES, PROTECTED_DEFAULT_PROPERTY_FIELDS } from 'ContentUtils/constants/Translations';
/**
 * PLEASE READ BEFORE MAKING CHANGES
 *
 * Any changes made in this file, must also be made here to maintain consistency
 * https://git.hubteam.com/HubSpot/CmsAssets/blob/master/bin/helpers/TranslationHelpers.js
 */

function generateFieldTranslationMap(field) {
  const attributes = BASE_ATTRIBUTES.concat(ADDITIONAL_ATTRIBUTES[field.type]).filter(Boolean);
  const baseTranslationMap = attributes.reduce((hash, key) => {
    /* Don't translate defaults for group */
    if (key === DEFAULT && (field.type === GROUP || PROTECTED_DEFAULT_PROPERTY_FIELDS[field.type])) return hash;
    const fieldValue = field[key];

    if (key === DEFAULT && Array.isArray(fieldValue)) {
      hash[key] = fieldValue[0] || '';
    } else if (typeof fieldValue === 'object' && fieldValue && SPECIAL_DEFAULT_CHILD_PROPERTIES[field.type]) {
      /* Handle special cases for logo, img, and form */
      hash[key] = pick(fieldValue, Object.keys(SPECIAL_DEFAULT_CHILD_PROPERTIES[field.type]));
    } else {
      hash[key] = fieldValue || '';
    }

    return hash;
  }, {});

  switch (field.type) {
    case GROUP:
      {
        const {
          children
        } = field;
        if (!children) break;
        children.forEach(child => {
          baseTranslationMap[child.name] = generateFieldTranslationMap(child);
        });
        break;
      }

    case CHOICE:
      {
        baseTranslationMap[CHOICES] = field[CHOICES].reduce((hash, choice) => {
          hash[choice[0]] = choice[1];
          return hash;
        }, {});
        break;
      }

    default:
      break;
  }

  return baseTranslationMap;
}

function flatten(data, initialKey, flatMap = {}, includeDescription = false) {
  for (const key of Object.keys(data)) {
    const flatKey = initialKey ? `${initialKey}.${key}` : key;

    if (typeof data[key] === 'object' && data[key]) {
      flatten(data[key], flatKey, flatMap, includeDescription);
    }

    if (typeof data[key] === 'string' || !data[key]) {
      const translation = {};

      if (includeDescription) {
        translation.description = '';
      }

      translation.message = data[key] || '';
      flatMap[flatKey] = translation;
    }
  }

  return flatMap;
}

export function generateTranslationsFromFields(customWidgetLabel, fields, includeDescription = false) {
  return flatten(fields.reduce((hash, field) => {
    hash[field.name] = generateFieldTranslationMap(field);
    return hash;
  }, {
    name: customWidgetLabel
  }), null, {}, includeDescription);
}
export function generateTranslationsFile(messages) {
  return JSON.stringify(messages, null, 2);
}

function getDefaultPaths(defaultObject, initialPath = '') {
  if (typeof defaultObject === 'string') return defaultObject;
  const paths = [];

  for (const key of Object.keys(defaultObject)) {
    if (typeof defaultObject[key] === 'object' && defaultObject[key]) {
      const childPaths = getDefaultPaths(defaultObject[key], key);

      for (const childPath of childPaths) {
        paths.push(`${initialPath}.${childPath}`);
      }
    } else {
      paths.push(`${initialPath}.${key}`);
    }
  }

  return paths;
}

export function generateAllPaths(field, initialPath = '') {
  /* If we have an initial path, use that as the base for all paths */
  let attributes = BASE_ATTRIBUTES.concat(ADDITIONAL_ATTRIBUTES[field.type]).filter(Boolean);
  attributes = field.type === GROUP ? attributes.concat(CHILDREN) : attributes;
  const paths = [];

  for (const key of attributes) {
    if (key === CHILDREN) {
      for (const child of field[key]) {
        const childPaths = generateAllPaths(child, child.name);

        for (const childPath of childPaths) {
          paths.push(`${initialPath}.${childPath}`);
        }
      }

      continue;
    }
    /* TODO: Update these as well */


    if (key === DEFAULT && typeof field[key] === 'object' && field[key] && !PROTECTED_DEFAULT_PROPERTY_FIELDS[field.type] && field.type !== GROUP) {
      const defaultPaths = getDefaultPaths(pick(field[key], Object.keys(SPECIAL_DEFAULT_CHILD_PROPERTIES[field.type])));

      for (const defaultPath of defaultPaths) {
        paths.push(`${initialPath}.${key}${defaultPath}`);
      }

      continue;
    }

    if (key === CHOICES) {
      for (const choice of field[key]) {
        paths.push(`${initialPath}.${key}.${choice[0]}`);
      }

      continue;
    }

    if (!TRANSLATABLE_FIELD_ATTRIBUTES[key]) continue;
    /* All that is left is regular attributes */

    paths.push(`${initialPath}.${key}`);
  }

  return paths;
}